import React, { useCallback, useMemo, useState } from 'react';
import {
  AutoAwesomeOutlined,
  DashboardOutlined,
  DescriptionOutlined,
  FolderOutlined,
  LockOutlined,
  RouteOutlined,
} from '@mui/icons-material';
import { Box, Typography, useMediaQuery } from '@mui/material';
import { Button, Card, FormConfirmDialog, Tabs } from '../../../../components/ui';
import { useFeatureTheme } from '../../../../theme/hooks/useFeatureTheme';
import {
  FORM_STUDIO_COPY,
  FORM_STUDIO_SECTIONS,
} from '../constants/formStudioConfig';
import {
  getFormStudioSection,
  isFormStudioSectionLocked,
} from '../utils/formStudioSections';
import FormStudioSectionContent from './FormStudioSectionContent';
import { spacing } from '../../../../theme/tokens';

const SECTION_ICONS = {
  overview: DashboardOutlined,
  fields: DescriptionOutlined,
  workflow: RouteOutlined,
  automations: AutoAwesomeOutlined,
  assets: FolderOutlined,
};

export default function FormStudioShell({
  mode,
  activeSection,
  formName = '',
  isLoading = false,
  loadError = '',
  isDirty = false,
  isSubmitting = false,
  hasFormId = false,
  studio,
  workspaceId,
  formId,
  showCreateSuccess = false,
  onDismissCreateSuccess,
  onCreateSuccessAddFields,
  onSectionChange,
  onLockedSectionAttempt,
  onBack,
  onSave,
  onDelete,
  onReload,
}) {
  const { semantic, radiusPx, shadows, typography, typographyStyles, muiTheme } =
    useFeatureTheme();
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'));
  const pageCopy = FORM_STUDIO_COPY[mode];
  const section = getFormStudioSection(activeSection);
  const saveLabel = hasFormId ? FORM_STUDIO_COPY.save : FORM_STUDIO_COPY.createForm;
  const isDeleting = Boolean(studio?.isDeleting);
  const canDeleteForm = hasFormId && typeof onDelete === 'function';

  const handleSectionRequest = useCallback(
    (nextSection) => {
      if (isFormStudioSectionLocked(nextSection, hasFormId)) {
        onLockedSectionAttempt?.();
        return;
      }
      onSectionChange?.(nextSection);
    },
    [hasFormId, onLockedSectionAttempt, onSectionChange]
  );

  const mobileTabs = useMemo(
    () => FORM_STUDIO_SECTIONS.map((item) => ({
      ...item,
      label: isFormStudioSectionLocked(item.value, hasFormId)
        ? `${item.label} (locked)`
        : item.label,
    })),
    [hasFormId]
  );

  const renderNavButton = (item) => {
    const Icon = SECTION_ICONS[item.icon];
    const isActive = activeSection === item.value;
    const isLocked = isFormStudioSectionLocked(item.value, hasFormId);

    return (
      <Box
        key={item.value}
        component="button"
        type="button"
        onClick={() => handleSectionRequest(item.value)}
        aria-current={isActive ? 'page' : undefined}
        aria-disabled={isLocked || undefined}
        title={isLocked ? FORM_STUDIO_COPY.sectionLocked : undefined}
        sx={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          gap: 1.25,
          px: 1.5,
          py: 1.25,
          mb: 0.5,
          border: `1px solid ${
            isActive ? semantic.interactive.primaryBorder : semantic.surfaces.surface
          }`,
          borderRadius: radiusPx.md,
          bgcolor: isActive
            ? semantic.interactive.primarySoft
            : semantic.surfaces.surface,
          color: isLocked
            ? semantic.text.muted
            : isActive
              ? semantic.interactive.primary
              : semantic.text.secondary,
          fontFamily: typography.fontFamilyBase,
          fontSize: typography.fontSizeMd,
          fontWeight: isActive
            ? typography.fontWeightSemibold
            : typography.fontWeightMedium,
          textAlign: 'left',
          cursor: isLocked ? 'not-allowed' : 'pointer',
          opacity: isLocked ? 0.72 : 1,
          '&:hover': {
            bgcolor: isLocked ? semantic.surfaces.surface : semantic.surfaces.surfaceMuted,
          },
          '&:focus-visible': {
            outline: 'none',
            boxShadow: `0 0 0 3px ${semantic.borders.focusRing}`,
          },
        }}
      >
        <Icon fontSize="small" />
        <Box component="span" sx={{ flex: 1 }}>
          {item.label}
        </Box>
        {isLocked ? <LockOutlined sx={{ fontSize: 16, opacity: 0.8 }} /> : null}
      </Box>
    );
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        minHeight: 0,
        bgcolor: semantic.surfaces.background,
        border: `1px solid ${semantic.borders.border}`,
        borderRadius: radiusPx.xl,
        overflow: 'hidden',
        marginTop:{xs:spacing.size8 , sm:'auto'}
      }}
    >
      <Box
        component="header"
        sx={{
          display: 'flex',
          alignItems: { xs: 'flex-start', sm: 'center' },
          justifyContent: 'space-between',
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 2,
          px: { xs: 2, md: 3 },
          py: 2,
          bgcolor: semantic.surfaces.surface,
          borderBottom: `1px solid ${semantic.borders.border}`,
        }}
      >
        <Box>
          <Typography
            sx={{
              color: semantic.interactive.primary,
              fontSize: typography.fontSizeXs,
              fontWeight: typography.fontWeightSemibold,
              textTransform: 'uppercase',
              letterSpacing: '0.08em',
            }}
          >
            {pageCopy.eyebrow}
          </Typography>
          <Typography component="h1" sx={{ ...typographyStyles.pageTitle, color: semantic.text.primary }}>
            {formName?.trim() || pageCopy.title}
          </Typography>
          <Typography sx={{ ...typographyStyles.bodyText, color: semantic.text.secondary }}>
            {pageCopy.subtitle}
          </Typography>
          {isDirty ? (
            <Typography
              sx={{
                mt: 0.5,
                color: semantic.states.warning,
                fontSize: typography.fontSizeSm,
                fontWeight: typography.fontWeightMedium,
              }}
            >
              {FORM_STUDIO_COPY.dirty}
            </Typography>
          ) : null}
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button variant="outline" onClick={onBack} disabled={isSubmitting || isDeleting}>
            {FORM_STUDIO_COPY.back}
          </Button>
          <Button
            variant="primary"
            onClick={onSave}
            disabled={isLoading || isSubmitting || isDeleting || Boolean(loadError)}
          >
            {isSubmitting ? FORM_STUDIO_COPY.saving : saveLabel}
          </Button>
          {canDeleteForm ? (
            <Button
              variant="destructive"
              onClick={() => setDeleteConfirmOpen(true)}
              disabled={isLoading || isSubmitting || isDeleting || Boolean(loadError)}
            >
              {isDeleting ? FORM_STUDIO_COPY.deleting : FORM_STUDIO_COPY.deleteForm}
            </Button>
          ) : null}
        </Box>
      </Box>

      {isMobile ? (
        <Box
          component="nav"
          aria-label={FORM_STUDIO_COPY.navigationLabel}
          sx={{
            p: 1.5,
            bgcolor: semantic.surfaces.surface,
            borderBottom: `1px solid ${semantic.borders.border}`,
            overflowX: 'auto',
          }}
        >
          <Tabs
            value={activeSection}
            onChange={handleSectionRequest}
            tabs={mobileTabs}
            ariaLabel={FORM_STUDIO_COPY.navigationLabel}
            sx={{ minWidth: 650 }}
          />
        </Box>
      ) : null}

      <Box sx={{ display: 'flex', flex: 1, minHeight: 0 }}>
        {!isMobile ? (
          <Box
            component="nav"
            aria-label={FORM_STUDIO_COPY.navigationLabel}
            sx={{
              width: 240,
              flexShrink: 0,
              p: 2,
              bgcolor: semantic.surfaces.surface,
              borderRight: `1px solid ${semantic.borders.border}`,
            }}
          >
            {FORM_STUDIO_SECTIONS.map(renderNavButton)}
          </Box>
        ) : null}

        <Box
          component="main"
          sx={{
            flex: 1,
            minWidth: 0,
            overflow: 'auto',
            p: { xs: 2, md: 3 },
          }}
        >
          <Card
            sx={{
              maxWidth: 1120,
              minHeight: 280,
              boxShadow: shadows.sm,
              p: { xs: 2.5, md: 4 },
            }}
          >
            <Typography component="h2" sx={{ ...typographyStyles.sectionTitle, color: semantic.text.primary }}>
              {section.title}
            </Typography>
            <Typography sx={{ ...typographyStyles.bodyText, color: semantic.text.secondary, mt: 1, mb: 2 }}>
              {section.description}
            </Typography>
            {isLoading ? (
              <Typography sx={{ ...typographyStyles.bodyText, color: semantic.text.muted }}>
                Loading form…
              </Typography>
            ) : null}
            {!isLoading && loadError ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Typography sx={{ ...typographyStyles.bodyText, color: semantic.states.danger }}>
                  {loadError || FORM_STUDIO_COPY.loadError}
                </Typography>
                {onReload ? (
                  <Box>
                    <Button variant="outline" onClick={onReload}>
                      {FORM_STUDIO_COPY.reload}
                    </Button>
                  </Box>
                ) : null}
              </Box>
            ) : null}
            {!isLoading && !loadError && studio ? (
              <FormStudioSectionContent
                activeSection={activeSection}
                studio={studio}
                workspaceId={workspaceId}
                formId={formId}
                hasFormId={hasFormId}
                showCreateSuccess={showCreateSuccess}
                onDismissCreateSuccess={onDismissCreateSuccess}
                onCreateSuccessAddFields={onCreateSuccessAddFields}
                onSectionChange={handleSectionRequest}
              />
            ) : null}
          </Card>
        </Box>
      </Box>
      <FormConfirmDialog
        open={deleteConfirmOpen}
        onCancel={() => {
          if (!isDeleting) {
            setDeleteConfirmOpen(false);
          }
        }}
        onConfirm={async () => {
          await onDelete?.();
          setDeleteConfirmOpen(false);
        }}
        title={FORM_STUDIO_COPY.deleteTitle}
        message={FORM_STUDIO_COPY.deleteMessage}
        confirmLabel={FORM_STUDIO_COPY.deleteConfirm}
        cancelLabel={FORM_STUDIO_COPY.deleteCancel}
        confirmLoading={isDeleting}
      />
    </Box>
  );
}
